/**
 * Video render-source resolution (BAL-721 / R3, R4) — THE single decision
 * function shared by every video render surface (VideoTile, DancerReel, and the
 * media-picker cluster). Given a media row's Mux + R2 + fallback fields it
 * decides HOW to render, so the branching is unit-tested once and every
 * component stays thin. Lives in `@kit/uploads` (not the app) so the picker
 * cluster — which is a shared package and cannot import app code — uses the
 * SAME resolver as the app surfaces (Rule #8: one edit surface).
 *
 *   - `mux`        → a `mux_playback_id` exists AND the encode is `ready`: play
 *                    Mux HLS via a signed, short-lived token (the browser-safe
 *                    path that fixes the broken .mov corpus and the migrated
 *                    videos whose Supabase-Storage object is gone).
 *   - `processing` → the asset is still encoding (`mux_status='preparing'`):
 *                    show a processing state, never a broken player (R4).
 *   - `r2`         → a public, non-Mux video that has been copied to R2
 *                    (`r2_key` set): play it from the immutable cdn.ballee.co
 *                    origin instead of a signed Supabase-Storage URL.
 *   - `external`   → a YouTube/Vimeo/etc host link: pass-through, untouched.
 *   - `native`     → an uploaded clip with no (or errored) Mux asset and no R2
 *                    copy: fall back to the native `<video>` element (R3
 *                    fallback; the errored edge is surfaced separately for
 *                    reconciliation, R5).
 *   - `none`       → nothing renderable.
 *
 * External links take precedence: they are out of scope for Mux/R2 and always
 * render as their host embed. Among uploaded clips, a ready Mux asset wins; a
 * preparing one shows processing; a public R2 copy wins over the signed-storage
 * native fallback; anything else falls back to native.
 */
export type VideoRenderSource =
  | { kind: 'mux'; playbackId: string }
  | { kind: 'processing' }
  | { kind: 'r2'; url: string }
  | { kind: 'external'; url: string }
  | { kind: 'native'; url: string }
  | { kind: 'none' };

/** Public origin for R2-migrated media (immutable originals). Matches the
 * prefix legacy R2 rows already store in storage_path (see media-url.ts). */
const R2_PUBLIC_ORIGIN = 'https://cdn.ballee.co';

export interface VideoSourceInput {
  /** External host link (YouTube / Vimeo / …). */
  externalUrl?: string | null;
  /** Fetchable http(s) URL for an uploaded clip (signed / public storage). */
  uploadedUrl?: string | null;
  /** Mux playback id, present once `video.asset.ready` has fired. */
  muxPlaybackId?: string | null;
  /** Mux encode lifecycle: 'preparing' | 'ready' | 'errored' | null. */
  muxStatus?: string | null;
  /** R2 object key (e.g. `artist-media/<uuid>/video/x.mp4`) once the immutable
   * original has been copied to R2. When set on a public, non-Mux row the clip
   * is served from cdn.ballee.co rather than a signed Supabase-Storage URL. */
  r2Key?: string | null;
}

function isHttpUrl(value: string | null | undefined): value is string {
  return (
    typeof value === 'string' &&
    (value.startsWith('http://') || value.startsWith('https://'))
  );
}

/** Build the stable, token-free cdn.ballee.co URL for an R2 video object. */
export function r2VideoUrl(r2Key: string): string {
  return `${R2_PUBLIC_ORIGIN}/${r2Key.replace(/^\/+/, '')}`;
}

export function resolveVideoSource(input: VideoSourceInput): VideoRenderSource {
  // External hosts are never routed through Mux/R2 — pass them through untouched.
  if (isHttpUrl(input.externalUrl)) {
    return { kind: 'external', url: input.externalUrl };
  }

  // A finished Mux encode is the preferred, browser-safe playback path.
  if (input.muxPlaybackId && input.muxStatus === 'ready') {
    return { kind: 'mux', playbackId: input.muxPlaybackId };
  }

  // Still encoding — show a processing state rather than a broken player (R4).
  // Checked before R2/native so a just-uploaded .mov (broken in Chrome/Firefox)
  // does not flash as a broken tile while Mux is encoding.
  if (input.muxStatus === 'preparing') {
    return { kind: 'processing' };
  }

  // A public R2 copy — serve the immutable cdn.ballee.co original instead of a
  // signed Supabase-Storage URL that 400s once the storage object is gone.
  const r2Key = input.r2Key?.trim();
  if (r2Key) {
    return { kind: 'r2', url: r2VideoUrl(r2Key) };
  }

  // No usable Mux asset (never ingested, or errored) and no R2 copy → native
  // element from the signed storage URL (R3).
  if (isHttpUrl(input.uploadedUrl)) {
    return { kind: 'native', url: input.uploadedUrl };
  }

  return { kind: 'none' };
}
